import React, { useState } from 'react';
import { isWithinServiceArea, getDistanceFromCenter, SERVICE_AREA } from '../config/serviceArea';

interface LocationPermissionProps {
  onLocationGranted: (location: { lat: number; lng: number }) => void;
}

const LocationPermission: React.FC<LocationPermissionProps> = ({ onLocationGranted }) => {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const handleEnableLocation = () => {
    setChecking(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setChecking(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Check if location is within service area
        if (isWithinServiceArea(latitude, longitude)) {
          // Location is serviceable
          onLocationGranted({ lat: latitude, lng: longitude });
        } else {
          // Location is outside service area
          const distance = getDistanceFromCenter(latitude, longitude);
          setError(
            `Sorry! We don't serve your location yet.\n\n` +
            `Your location is ${distance.toFixed(1)} km away from ${SERVICE_AREA.name}.\n` +
            `We currently deliver within ${SERVICE_AREA.radius} km radius.\n\n` +
            `We're expanding soon! Stay tuned.`
          );
          setChecking(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your location. ';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access in your browser settings to use this app.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable. Please check your device settings.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage += 'An unknown error occurred.';
        }
        
        setError(errorMessage);
        setChecking(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.iconContainer}>
          <div style={styles.icon}>📍</div>
        </div>
        
        <h1 style={styles.title}>Enable Location</h1>
        
        <p style={styles.description}>
          We need your location to check if we deliver to your area and provide you with the best service.
        </p>

        <div style={styles.serviceInfo}>
          <div style={styles.serviceItem}>
            <span style={styles.serviceIcon}>🏙️</span>
            <span style={styles.serviceText}>Serving {SERVICE_AREA.name}</span>
          </div>
          <div style={styles.serviceItem}>
            <span style={styles.serviceIcon}>📏</span>
            <span style={styles.serviceText}>{SERVICE_AREA.radius} km delivery radius</span>
          </div>
          <div style={styles.serviceItem}>
            <span style={styles.serviceIcon}>🚚</span>
            <span style={styles.serviceText}>Fast & reliable delivery</span>
          </div>
        </div>

        {error && (
          <div style={styles.errorBox}>
            <div style={styles.errorIcon}>❌</div>
            <div style={styles.errorText}>{error}</div>
          </div>
        )}

        <button
          style={{
            ...styles.button,
            opacity: checking ? 0.7 : 1,
            cursor: checking ? 'not-allowed' : 'pointer'
          }}
          onClick={handleEnableLocation}
          disabled={checking}
        >
          {checking ? (
            <>
              <span style={styles.spinner}>⏳</span> Checking Location...
            </>
          ) : (
            <>
              <span style={styles.buttonIcon}>📍</span> Enable Location Access
            </>
          )}
        </button>

        <p style={styles.privacy}>
          🔒 Your location is only used to verify service availability and improve delivery experience. We respect your privacy.
        </p>
      </div>
    </div>
  );
};

const styles: any = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  content: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px 30px',
    maxWidth: '450px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: '20px',
  },
  icon: {
    fontSize: '80px',
    animation: 'bounce 2s infinite',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '15px',
  },
  description: {
    fontSize: '16px',
    color: '#7f8c8d',
    lineHeight: '1.6',
    marginBottom: '30px',
  },
  serviceInfo: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '25px',
  },
  serviceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    fontSize: '15px',
    color: '#34495e',
  },
  serviceIcon: {
    fontSize: '24px',
    marginRight: '10px',
  },
  serviceText: {
    fontWeight: '500',
  },
  button: {
    width: '100%',
    padding: '18px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  },
  buttonIcon: {
    fontSize: '22px',
  },
  spinner: {
    fontSize: '22px',
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  privacy: {
    fontSize: '13px',
    color: '#95a5a6',
    marginTop: '20px',
    lineHeight: '1.5',
  },
  errorBox: {
    background: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
    border: '2px solid #fc8181',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    textAlign: 'left',
  },
  errorIcon: {
    fontSize: '32px',
    textAlign: 'center',
    marginBottom: '10px',
  },
  errorText: {
    color: '#c53030',
    fontSize: '14px',
    lineHeight: '1.6',
    whiteSpace: 'pre-line',
    textAlign: 'center',
  },
};

// Add keyframe animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default LocationPermission;
